import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Zap, Link2, QrCode, Radar, Webhook, Palette,
  LayoutDashboard, Banknote, RefreshCw, ShieldCheck, Globe, Code2,
} from "lucide-react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import LiveDemo from "@/components/LiveDemo";
import CoinIcon from "@/components/CoinIcon";
import { Button } from "@/components/ui/button";

const FEATURES = [
  { icon: Zap, title: "Kripto Ödemeleri", desc: "BTC, ETH, USDT ve daha fazlasını saniyeler içinde kabul edin." },
  { icon: Link2, title: "Ödeme Linkleri", desc: "Kod yazmadan paylaşılabilir ödeme linkleri oluşturun." },
  { icon: QrCode, title: "QR ile Ödeme", desc: "Mobil uyumlu QR kod ile hızlı ve temassız ödeme." },
  { icon: Radar, title: "Anında Ödeme Algılama", desc: "Blockchain üzerinde ödemeler gerçek zamanlı algılanır." },
  { icon: Webhook, title: "API & Webhook", desc: "Güçlü REST API ve imzalı webhook'lar ile tam entegrasyon." },
  { icon: Palette, title: "Özelleştirilebilir Checkout", desc: "Markanıza uygun ödeme sayfası tasarlayın." },
  { icon: LayoutDashboard, title: "Merchant Dashboard", desc: "Tüm ödemelerinizi tek panelden yönetin." },
  { icon: Banknote, title: "Kripto Para Çekme", desc: "Bakiyenizi güvenle kendi cüzdanınıza çekin." },
  { icon: RefreshCw, title: "Otomatik Ödeme Takibi", desc: "Ödeme durumları otomatik güncellenir." },
];

const COINS = ["BTC", "ETH", "USDT", "USDC", "SOL"];

export default function Home() {
  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {/* Hero */}
      <section className="relative overflow-hidden grid-bg">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-24 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Türkiye merkezli kripto ödeme altyapısı
              </span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]">
                Kripto Ödemeleri
                <br />
                <span className="text-primary">Basitleştirin.</span>
              </h1>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-lg">
                Coinbuy ile işletmeniz için hızlı, güvenli ve özelleştirilebilir kripto ödeme altyapısı oluşturun.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register">
                  <Button size="lg" data-testid="hero-start-btn" className="rounded-full h-12 px-7 text-sm font-semibold group">
                    Hemen Başla
                    <ArrowRight size={17} className="transition-transform group-hover:translate-x-0.5" />
                  </Button>
                </Link>
                <Link to="/docs">
                  <Button size="lg" variant="outline" data-testid="hero-docs-btn" className="rounded-full h-12 px-7 text-sm font-semibold">
                    Dokümantasyonu İncele
                  </Button>
                </Link>
              </div>
              <div className="mt-10">
                <p className="text-xs text-muted-foreground mb-3">Desteklenen kripto paralar</p>
                <div className="flex items-center gap-3">
                  {COINS.map((c) => (
                    <div key={c} className="flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1.5">
                      <CoinIcon symbol={c} size={20} />
                      <span className="text-xs font-semibold">{c}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <LiveDemo />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-20 sm:py-28">
        <div className="max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter">
            Kripto ödeme için ihtiyacınız olan her şey
          </h2>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed">
            Ödeme kabul etmekten para çekmeye, API entegrasyonundan özelleştirilebilir checkout'a kadar eksiksiz bir altyapı.
          </p>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-[border-color,box-shadow]"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                <f.icon size={20} strokeWidth={1.5} />
              </span>
              <h3 className="mt-4 text-base font-bold">{f.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trust band */}
      <section className="border-y border-border bg-card/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 grid sm:grid-cols-3 gap-8">
          {[
            { icon: ShieldCheck, t: "Banka seviyesi güvenlik", d: "2FA, imzalı webhook ve idempotency ile korunan altyapı." },
            { icon: Globe, t: "Türkiye'ye özel", d: "TL fiyatlandırma ve tamamen Türkçe deneyim." },
            { icon: Code2, t: "Geliştirici dostu", d: "Net dokümantasyon ve modern REST API." },
          ].map((x) => (
            <div key={x.t} className="flex gap-4">
              <span className="shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <x.icon size={20} strokeWidth={1.5} />
              </span>
              <div>
                <h3 className="text-base font-bold">{x.t}</h3>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{x.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-24">
        <div className="relative overflow-hidden rounded-3xl bg-primary px-8 py-16 sm:px-16 text-center">
          <div className="absolute inset-0 grid-bg opacity-10" aria-hidden />
          <h2 className="relative text-3xl sm:text-4xl font-extrabold tracking-tighter text-white">
            Kripto ödemeleri kabul etmeye bugün başlayın
          </h2>
          <p className="relative mt-4 text-base text-white/80 max-w-xl mx-auto">
            Dakikalar içinde hesabınızı oluşturun ve ilk ödeme linkinizi paylaşın.
          </p>
          <div className="relative mt-8">
            <Link to="/register">
              <Button size="lg" data-testid="cta-start-btn" className="rounded-full h-12 px-8 bg-white text-primary hover:bg-white/90 font-semibold">
                Ücretsiz Hesap Oluştur
                <ArrowRight size={17} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
